import { FormHints, HintProps } from '../FormHints/FormHints.component';
import { Label, LabelProps } from '../Label/Label.component';
import React, { ComponentPropsWithRef, FC, forwardRef, useEffect, useMemo, useState } from 'react';
import { CalendarIcon } from '@components/icons/Calendar.icon';
import { FormElement, Validator } from '@form/business/form.types';
import { useTranslation } from '@locales/useTranslation.hook';
import { KeyboardDatePicker, KeyboardDatePickerProps } from '@material-ui/pickers';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { useClassNames } from '@styles/breakpoints';
import { isServer } from '@utils/server';
import { Dayjs } from 'dayjs';

type Props = Omit<
  KeyboardDatePickerProps,
  'keyboardIcon' | 'variant' | 'helperText' | 'onError' | 'error' | 'onBlur' | 'ref'
> &
  HintProps &
  LabelProps &
  FormElement & {
    hintI18n?: string;
    withoutModale?: boolean;
  } & Pick<ComponentPropsWithRef<'div'>, 'ref'>;

const PlcDatePicker: FC<Props> = forwardRef(
  (
    {
      className,
      inputVariant = 'outlined',
      disableToolbar = true,
      format = 'DD/MM/YYYY',
      labelI18n,
      label,
      disabled,
      required,
      labelFlavour,
      labelVariant,
      hintI18n,
      isHintVisible,
      validators,
      cancelLabel,
      okLabel,
      autoOk = true,
      invalidDateMessage,
      maxDateMessage,
      minDateMessage,
      fullWidth,
      variant,
      value,
      onChange,
      minDate,
      maxDate,
      withoutModale = false,
      ...rest
    },
    datePickerRef
  ) => {
    const rootClass = useClassNames({ all: ['plc-form-element', variant, className] });
    const { t } = useTranslation(['common']);
    const [currentValue, setCurrentValue] = useState(value);

    const dateValidators: Validator[] = useMemo(
      () => [
        {
          isValid: currentValue ? (currentValue as Dayjs).isValid() : true,
          message: (invalidDateMessage as string) || t('common.form.invalid-date'),
        },
        {
          isValid:
            maxDate && currentValue
              ? (currentValue as Dayjs).isBefore(maxDate as Dayjs, 'day') ||
                (currentValue as Dayjs).isSame(maxDate as Dayjs, 'day')
              : true,
          message: (maxDateMessage as string) || t('common.form.max-date-message'),
        },
        {
          isValid:
            minDate && currentValue
              ? (currentValue as Dayjs).isAfter(minDate as Dayjs, 'day') ||
                (currentValue as Dayjs).isSame(minDate as Dayjs, 'day')
              : true,
          message: (minDateMessage as string) || t('common.form.min-date-message'),
        },
      ],
      [currentValue, invalidDateMessage, maxDate, maxDateMessage, minDate, minDateMessage]
    );
    const fullValidators = useMemo(() => {
      const validatorsCopy = [...dateValidators];
      if (validators) {
        validatorsCopy.push(...validators);
      }
      return validatorsCopy;
    }, [validators, dateValidators]);

    const isError = useMemo(() => !fullValidators.every(v => v.isValid), [fullValidators]);
    const isDateError = useMemo(() => !dateValidators.every(v => v.isValid), [dateValidators]);

    function handleOnChange(newDate: MaterialUiPickersDate) {
      setCurrentValue(newDate);
    }

    useEffect(() => {
      if (isDateError) {
        return onChange(null);
      }
      return onChange(currentValue as MaterialUiPickersDate);
    }, [currentValue]);

    function handleModalOpening() {
      if (isServer()) {
        return;
      }
      setTimeout(() => {
        const actionButtons = document.querySelectorAll('.plc-date-picker-dialog .MuiDialogActions-root button');
        if (actionButtons) {
          actionButtons.forEach((actionButton, index) => {
            const textSpan = actionButton.querySelector('span');
            if (textSpan) {
              // eslint-disable-next-line no-param-reassign
              actionButton.className += `basic-button animated-button flat-button ${
                index === 0 ? 'secondary' : 'primary'
              } small`;
              // eslint-disable-next-line no-param-reassign
              textSpan.className += `typography small tiny ${index === 0 ? 'dark' : 'light'} left capitalize`;
            }
          });
        }
      }, 100);
    }

    return (
      <div ref={datePickerRef} className={rootClass}>
        <label>
          {(label || labelI18n) && (
            <header className="plc-mb-s">
              <Label
                label={label}
                labelFlavour={disabled ? 'grey' : labelFlavour}
                labelI18n={labelI18n}
                labelVariant={labelVariant}
                required={required}
              />
            </header>
          )}
          <KeyboardDatePicker
            autoOk={autoOk}
            cancelLabel={cancelLabel || t('common.cancel')}
            DialogProps={{ className: 'plc-date-picker-dialog' }}
            disabled={disabled}
            disableToolbar={disableToolbar}
            error={isError && isHintVisible}
            format={format}
            helperText=""
            InputAdornmentProps={withoutModale ? { style: { display: 'none' } } : undefined}
            inputProps={{ inputMode: 'numeric' }}
            inputVariant={inputVariant}
            keyboardIcon={<CalendarIcon />}
            maxDate={maxDate}
            minDate={minDate}
            okLabel={okLabel || t('common.validate')}
            onChange={handleOnChange}
            onOpen={handleModalOpening}
            value={currentValue || null}
            {...rest}
          />
          <FormHints isHintVisible={isHintVisible && !disabled} validators={fullValidators} />
        </label>
      </div>
    );
  }
);

export { PlcDatePicker };
